import React from "react";

import { useQuery } from "@tanstack/react-query";

import { Callout } from "src/design-system/Callout";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useNodeRunState } from "src/store/runState/RunState";
import { fetchTestRunTrimmingReport, TrimmingReport } from "src/utils/testRun";

type Props = {
  testRunId: string;
  versionId: string;
  nodeId: string;
};

export const hasTrimming = (
  report: TrimmingReport | undefined,
): report is TrimmingReport => {
  if (!report) return false;
  return (report.partially_trimmed ?? 0) + (report.fully_trimmed ?? 0) > 0;
};

export const formatTrimmingMessage = (report: TrimmingReport): string => {
  const partiallyTrimmed = report.partially_trimmed ?? 0;
  const fullyTrimmed = report.fully_trimmed ?? 0;
  const total = partiallyTrimmed + fullyTrimmed;

  return `${total} output value${total !== 1 ? "s were" : " was"} trimmed due to size limits. Test with a smaller dataset for detailed results.`;
};

const useTrimmingReport = ({
  testRunId,
  versionId,
  nodeId,
}: {
  testRunId: string;
  versionId: string;
  nodeId: string;
}) => {
  const { workspace } = useAuthoringContext();
  const baseUrl = workspace.base_url ?? "";

  return useQuery({
    queryKey: ["trimmingReport", baseUrl, testRunId, versionId, nodeId],
    queryFn: () =>
      fetchTestRunTrimmingReport({
        workspaceBaseUrl: baseUrl,
        run_id: testRunId,
        version_id: versionId,
        node_id: nodeId,
      }),
    enabled: Boolean(baseUrl && testRunId && versionId && nodeId),
    staleTime: Infinity,
    retry: false,
  });
};

export const TrimmingReportBanner: React.FC<Props> = ({
  testRunId,
  versionId,
  nodeId,
}) => {
  const { data: trimmingReport } = useTrimmingReport({
    testRunId,
    versionId,
    nodeId,
  });

  if (!hasTrimming(trimmingReport)) return null;

  return (
    <Callout dataLoc="trimming-report-banner" type="warning">
      {formatTrimmingMessage(trimmingReport)}
    </Callout>
  );
};

export const RunStateTrimmingBanner: React.FC<{
  nodeIdOrAlias?: string;
  dataLoc: string;
  className?: string;
}> = ({ nodeIdOrAlias = "output", dataLoc, className }) => {
  const runState = useNodeRunState(nodeIdOrAlias);
  const testResult =
    runState?.type === "test-run" ? runState.testResult : undefined;

  const { data: trimmingReport } = useTrimmingReport({
    testRunId: testResult?.test_run_id ?? "",
    versionId: testResult?.version_id ?? "",
    nodeId: testResult?.node_id ?? "",
  });

  if (!hasTrimming(trimmingReport)) return null;

  return (
    <div className={className}>
      <Callout dataLoc={dataLoc} type="warning">
        {formatTrimmingMessage(trimmingReport)}
      </Callout>
    </div>
  );
};
